import { Button, Spinner, useTranslation } from '@hbs-us/ui';
import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useEffect, useId, useRef, useState } from 'react';

import { RichTextEditor } from '../../components/RichTextEditorLazy.js';
import { apiFetch } from '../../lib/apiFetch.js';
import { useMoneyFormatters } from '../_money/useMoneyFormatters.js';
import { termsTextToHtml, useSettingsDefaults } from '../settings/useSettingsDefaults.js';
import {
  EstimateLineItemsEditor,
  type EstimateLineRow,
  newLineRow,
  rowLineTotal,
} from './components/EstimateLineItemsEditor.js';
import { estimateLineItemForForm } from './estimateFormData.js';
import { estimateQueryKey, useEstimateQuery } from './useEstimateQuery.js';

/**
 * Create/edit estimate editor (legacy `EstimateFormPage`), re-skinned to the
 * locked brand and wired to the api-host JSON write contract shipped in Plan
 * 21-10 (`POST /api/v1/estimates`, `PUT /api/v1/estimates/:id`).
 *
 * Sections mirror the prod estimate form: Estimate Information (status, tax rate
 * %, expiration), Customer Information, Scope of Work, Line Items (the editable
 * sub-component), Proposal Details, Terms. A live totals block previews
 * subtotal/tax/total client-side using the SAME sell-price rule as the server —
 * DISPLAY ONLY; the server recomputes authoritatively via the shared
 * `normalizeLineItems`/`calculateTotals` helpers (a client cannot forge totals).
 *
 * Submit goes through `apiFetch` (the double-submit CSRF token is baked in for
 * non-safe methods); the SPA never sends a tenant id (SEC-11). Every input has a
 * visible `<label htmlFor>` (A11Y-01); the primary CTA keeps its specific label
 * (`submitCreate` / `submitEdit`, never a bare "Save"). Every color is a locked
 * token (no hex — BRAND-06 / lint:no-hex).
 *
 * Attachment upload (W2) and the public approve/sign canvas (SSR, D-02) are
 * intentionally OUT of scope here.
 */

const editRouteApi = getRouteApi('/estimates/$id/edit');

/** Route wrapper for `/estimates/$id/edit` — reads the id param and prefills. */
export function EstimateEditRoute() {
  const { id } = editRouteApi.useParams();
  return <EstimateForm mode="edit" estimateId={Number(id)} />;
}

export interface EstimateFormProps {
  mode: 'create' | 'edit';
  /** Present in edit mode only (the `/estimates/$id/edit` route param). */
  estimateId?: number;
}

interface FormState {
  status: string;
  taxRatePercent: string;
  expirationDate: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  siteAddress: string;
  scopeOfWork: string;
  proposalTitle: string;
  paymentSchedule: string;
  customTerms: string;
  lineItems: EstimateLineRow[];
}

function blankState(): FormState {
  return {
    status: 'draft',
    taxRatePercent: '',
    expirationDate: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    siteAddress: '',
    scopeOfWork: '',
    proposalTitle: '',
    paymentSchedule: '',
    customTerms: '',
    lineItems: [newLineRow()],
  };
}

const INPUT_CLASS =
  'h-11 rounded-md border border-input bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

export function EstimateForm({ mode, estimateId }: EstimateFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fmt = useMoneyFormatters();

  const statusId = useId();
  const taxId = useId();
  const expirationId = useId();
  const nameId = useId();
  const emailId = useId();
  const phoneId = useId();
  const addressId = useId();
  const scopeId = useId();
  const proposalId = useId();
  const scheduleId = useId();
  const termsId = useId();

  const isEdit = mode === 'edit' && typeof estimateId === 'number' && estimateId > 0;
  const detailQuery = useEstimateQuery(estimateId ?? 0, isEdit);
  // CREATE-only: the workspace defaults from Settings (tax rate, proposal terms). A
  // convenience read — a Manager without settings.view gets a 403 and the form keeps its
  // blank defaults. Never fired in edit mode: an existing estimate owns its values.
  const settingsDefaults = useSettingsDefaults(!isEdit);

  const [form, setForm] = useState<FormState>(blankState);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const prefilled = useRef(false);
  const defaultsPrefilled = useRef(false);
  // Set when editing an estimate the customer already has. The status picker is replaced by a
  // note, because the server decides the status of such an edit (sent stays sent, rejected
  // reopens as ready) and a picker offering Draft would be a control that does nothing.
  const [withCustomer, setWithCustomer] = useState<'sent' | 'rejected' | null>(null);

  // Prefill once from the detail query in edit mode (guarded so a background
  // refetch never clobbers in-progress edits).
  useEffect(() => {
    if (!isEdit || prefilled.current) return;
    const estimate = detailQuery.data?.estimate;
    if (!estimate) return;
    prefilled.current = true;
    setWithCustomer(
      estimate.status === 'sent' || estimate.status === 'rejected' ? estimate.status : null,
    );
    setForm({
      status: estimate.status === 'ready' ? 'ready' : 'draft',
      taxRatePercent:
        typeof estimate.taxRatePercent === 'number' ? String(estimate.taxRatePercent) : '',
      expirationDate: estimate.expiresAt ?? '',
      customerName: estimate.customerName ?? '',
      customerEmail: estimate.customerEmail ?? '',
      customerPhone: estimate.customerPhone ?? '',
      siteAddress: estimate.siteAddress ?? '',
      scopeOfWork: estimate.scopeOfWork ?? '',
      proposalTitle: estimate.proposalTitle ?? '',
      paymentSchedule: estimate.paymentSchedule ?? '',
      customTerms: estimate.customTerms ?? '',
      lineItems: (estimate.lineItems ?? []).length
        ? estimate.lineItems.map(estimateLineItemForForm)
        : [newLineRow()],
    });
  }, [isEdit, detailQuery.data]);

  // Pre-fill the Settings defaults ONCE on create, and only into fields still at their blank
  // default — a late-resolving read must never clobber what the operator already typed. The
  // terms arrive as plain text and the editor stores HTML, hence the conversion.
  useEffect(() => {
    if (isEdit || defaultsPrefilled.current || !settingsDefaults) return;
    defaultsPrefilled.current = true;
    const { defaultTaxRate, proposalDefaultTerms } = settingsDefaults;
    setForm((prev) => ({
      ...prev,
      taxRatePercent:
        prev.taxRatePercent === '' && defaultTaxRate !== undefined
          ? String(defaultTaxRate)
          : prev.taxRatePercent,
      customTerms:
        prev.customTerms === '' && proposalDefaultTerms
          ? termsTextToHtml(proposalDefaultTerms)
          : prev.customTerms,
    }));
  }, [isEdit, settingsDefaults]);

  // Live totals preview (display only — server recomputes authoritatively).
  const subtotal = form.lineItems.reduce((sum, row) => sum + rowLineTotal(row), 0);
  const taxRate = Number(form.taxRatePercent) || 0;
  const tax = (subtotal * taxRate) / 100;
  const total = subtotal + tax;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    const body = {
      customerName: form.customerName,
      customerEmail: form.customerEmail || null,
      customerPhone: form.customerPhone || null,
      siteAddress: form.siteAddress || null,
      scopeOfWork: form.scopeOfWork || null,
      proposalTitle: form.proposalTitle || null,
      paymentSchedule: form.paymentSchedule || null,
      customTerms: form.customTerms || null,
      status: form.status,
      expirationDate: form.expirationDate || null,
      taxRatePercent: taxRate,
      lineItems: form.lineItems.map((row) => ({
        description: row.description,
        quantity: Number(row.quantity) || 0,
        unit: row.unit || null,
        baseCost: Number(row.baseCost) || 0,
        upchargePercent: Number(row.upchargePercent) || 0,
        applyUpcharge: row.applyUpcharge,
      })),
    };

    try {
      const path = isEdit ? `/api/v1/estimates/${estimateId}` : '/api/v1/estimates';
      const response = await apiFetch(path, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const result = (await response.json().catch(() => null)) as {
        ok?: boolean;
        error?: string;
        estimate?: { id: number };
      } | null;
      if (!response.ok || !result || result.ok === false) {
        // 409: the estimate moved on while this form was open — most often the customer approved
        // it — so it is no longer editable. Say that rather than showing the raw code.
        const conflict =
          result?.error === 'not_editable' || result?.error === 'estimate_not_editable';
        setError(
          conflict
            ? t('estimates:form.noLongerEditable')
            : (result?.error ?? t('estimates:form.genericError')),
        );
        return;
      }
      if (isEdit && typeof estimateId === 'number') {
        await queryClient.invalidateQueries({ queryKey: estimateQueryKey(estimateId) });
        void navigate({ to: '/estimates/$id', params: { id: String(estimateId) } });
      } else {
        const newId = result.estimate?.id;
        if (!newId) {
          setError(t('estimates:form.genericError'));
          return;
        }
        void navigate({ to: '/estimates/$id', params: { id: String(newId) } });
      }
    } catch {
      setError(t('estimates:form.genericError'));
    } finally {
      setSubmitting(false);
    }
  };

  const headingKey =
    mode === 'create' ? 'estimates:form.createHeading' : 'estimates:form.editHeading';
  const submitKey = mode === 'create' ? 'estimates:form.submitCreate' : 'estimates:form.submitEdit';

  if (isEdit && detailQuery.isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  if (isEdit && (detailQuery.isError || !detailQuery.data?.estimate)) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('estimates:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('estimates:error.body')}</p>
      </section>
    );
  }

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6">
        <h1 className="font-display font-semibold text-3xl text-foreground">{t(headingKey)}</h1>
      </header>

      {error ? (
        <div
          role="alert"
          className="mb-4 max-w-3xl rounded-md border border-destructive/30 bg-destructive/10 p-4 text-destructive text-sm"
        >
          {error}
        </div>
      ) : null}

      {withCustomer ? (
        <div
          role="note"
          className="mb-4 max-w-3xl rounded-md border border-border bg-muted p-4 text-foreground text-sm"
        >
          {t(
            withCustomer === 'sent'
              ? 'estimates:form.editingSentNote'
              : 'estimates:form.editingRejectedNote',
          )}
        </div>
      ) : null}

      <form onSubmit={onSubmit} className="flex max-w-3xl flex-col gap-4">
        {/* Estimate Information */}
        <section className="rounded-md border border-border bg-card p-6">
          <h2 className="mb-4 font-semibold text-foreground text-xl">
            {t('estimates:form.infoHeading')}
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="flex flex-col gap-1">
              <label htmlFor={statusId} className="font-medium text-foreground text-sm">
                {t('estimates:form.statusLabel')}
              </label>
              {withCustomer ? (
                <output id={statusId} className="flex h-11 items-center text-foreground text-sm">
                  {t(`estimates:status.${withCustomer}`)}
                </output>
              ) : (
                <select
                  id={statusId}
                  value={form.status}
                  onChange={(e) => set('status', e.target.value)}
                  className={INPUT_CLASS}
                >
                  <option value="draft">{t('estimates:status.draft')}</option>
                  <option value="ready">{t('estimates:status.ready')}</option>
                </select>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={taxId} className="font-medium text-foreground text-sm">
                {t('estimates:form.taxRate')}
              </label>
              <input
                id={taxId}
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={form.taxRatePercent}
                onChange={(e) => set('taxRatePercent', e.target.value)}
                className={`${INPUT_CLASS} font-mono`}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={expirationId} className="font-medium text-foreground text-sm">
                {t('estimates:form.expiration')}
              </label>
              <input
                id={expirationId}
                type="date"
                value={form.expirationDate}
                onChange={(e) => set('expirationDate', e.target.value)}
                className={`${INPUT_CLASS} font-mono`}
              />
            </div>
          </div>
        </section>

        {/* Customer Information */}
        <section className="rounded-md border border-border bg-card p-6">
          <h2 className="mb-4 font-semibold text-foreground text-xl">
            {t('estimates:form.customerHeading')}
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label htmlFor={nameId} className="font-medium text-foreground text-sm">
                {t('estimates:form.customerName')}
              </label>
              <input
                id={nameId}
                type="text"
                required
                value={form.customerName}
                onChange={(e) => set('customerName', e.target.value)}
                className={INPUT_CLASS}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={emailId} className="font-medium text-foreground text-sm">
                {t('estimates:form.customerEmail')}
              </label>
              <input
                id={emailId}
                type="email"
                value={form.customerEmail}
                onChange={(e) => set('customerEmail', e.target.value)}
                className={INPUT_CLASS}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={phoneId} className="font-medium text-foreground text-sm">
                {t('estimates:form.customerPhone')}
              </label>
              <input
                id={phoneId}
                type="tel"
                value={form.customerPhone}
                onChange={(e) => set('customerPhone', e.target.value)}
                className={INPUT_CLASS}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={addressId} className="font-medium text-foreground text-sm">
                {t('estimates:form.siteAddress')}
              </label>
              <input
                id={addressId}
                type="text"
                value={form.siteAddress}
                onChange={(e) => set('siteAddress', e.target.value)}
                className={INPUT_CLASS}
              />
            </div>
          </div>
        </section>

        {/* Scope of Work */}
        <section className="rounded-md border border-border bg-card p-6">
          <h2 className="mb-4 font-semibold text-foreground text-xl">
            {t('estimates:form.scopeHeading')}
          </h2>
          <div className="flex flex-col gap-1">
            <label htmlFor={scopeId} className="font-medium text-foreground text-sm">
              {t('estimates:form.scopeLabel')}
            </label>
            <RichTextEditor
              id={scopeId}
              placeholder={t('estimates:form.scopePlaceholder')}
              value={form.scopeOfWork}
              onChange={(html) => set('scopeOfWork', html)}
            />
          </div>
        </section>

        {/* Line Items */}
        <section className="rounded-md border border-border bg-card p-6">
          <h2 className="mb-4 font-semibold text-foreground text-xl">
            {t('estimates:form.lineItemsHeading')}
          </h2>
          <EstimateLineItemsEditor
            rows={form.lineItems}
            onChange={(rows) => set('lineItems', rows)}
            fmt={fmt}
          />
          {/* Live totals preview (display only). */}
          <dl className="mt-4 ml-auto grid max-w-xs gap-1 border-border border-t pt-4 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t('estimates:stats.subtotal')}</dt>
              <dd className="font-mono text-foreground">{fmt.formatCurrency(subtotal)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t('estimates:form.taxLabel')}</dt>
              <dd className="font-mono text-foreground">{fmt.formatCurrency(tax)}</dd>
            </div>
            <div className="flex justify-between gap-4 border-border border-t pt-1">
              <dt className="font-medium text-foreground">{t('estimates:form.totalLabel')}</dt>
              <dd className="font-medium font-mono text-foreground">{fmt.formatCurrency(total)}</dd>
            </div>
          </dl>
        </section>

        {/* Proposal Details */}
        <section className="rounded-md border border-border bg-card p-6">
          <h2 className="mb-4 font-semibold text-foreground text-xl">
            {t('estimates:form.proposalHeading')}
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label htmlFor={proposalId} className="font-medium text-foreground text-sm">
                {t('estimates:form.proposalTitle')}
              </label>
              <input
                id={proposalId}
                type="text"
                value={form.proposalTitle}
                onChange={(e) => set('proposalTitle', e.target.value)}
                className={INPUT_CLASS}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={scheduleId} className="font-medium text-foreground text-sm">
                {t('estimates:form.paymentSchedule')}
              </label>
              <input
                id={scheduleId}
                type="text"
                value={form.paymentSchedule}
                onChange={(e) => set('paymentSchedule', e.target.value)}
                className={INPUT_CLASS}
              />
            </div>
          </div>
        </section>

        {/* Terms */}
        <section className="rounded-md border border-border bg-card p-6">
          <h2 className="mb-4 font-semibold text-foreground text-xl">
            {t('estimates:form.termsHeading')}
          </h2>
          <div className="flex flex-col gap-1">
            <label htmlFor={termsId} className="font-medium text-foreground text-sm">
              {t('estimates:form.customTerms')}
            </label>
            <RichTextEditor
              id={termsId}
              value={form.customTerms}
              onChange={(html) => set('customTerms', html)}
            />
          </div>
        </section>

        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? t('estimates:form.saving') : t(submitKey)}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void navigate({ to: '/estimates' })}
          >
            {t('estimates:form.cancel')}
          </Button>
        </div>
      </form>
    </div>
  );
}
